import { IIconProps } from '@fluentui/react';
import { TFunction } from 'i18next';
import { CopilotAction, CopilotActionType, CopilotProcessStartInput } from '../copilotAction';
import { CopilotActiveField } from '../copilotField';
import { TenantProcessStatus } from '../tenantProcessStateBase';
import { AIActionKind, AIActionTemplate } from './aiActionTemplate';

/**
 * Where a binding sits in an AI actions menu, in render order.
 *
 * Inline acts on the page in front of the user. Assistant opens the Copilot panel. Background is an
 * unattended run the scene starts and polls itself. Other is what is left around such a run (clearing
 * its results): no header, at the bottom, under a separator.
 */
export enum AIActionGroup {
  Inline = 1,
  Assistant = 2,
  Background = 3,
  Other = 4,
}

export type AIActionBindingOptions = {
  // defaults to the group the template's kind implies: Inline, or Assistant for the panel kinds
  group?: AIActionGroup;
  // the linked context: the in-app route the action belongs to and navigates to before it runs
  route?: string;
  disabled?: boolean;
  title?: string;
  // Background: the run is going, so the item offers Stop and the button shows a spinner
  running?: boolean;
  icon?: IIconProps;
  // overrides the template's label on this surface (the selection wording of a prompt that takes one)
  label?: string;
  // the rows the scene has selected, carried to the panel by a Prompt binding whose template accepts a
  // selection; an empty array is the whole set
  selectedEntityIds?: number[];
  // Prompt: the general knowledge skill the request names (review-record, create-task, ...)
  skill?: string;
  // Prompt: the Library's selected row is referenced into the session for this prompt; unset means the prompt
  // is about the page
  usesSelectedDocument?: boolean;
};

/**
 * One template on one surface with concrete parameters. A level of the app (scene, tab, field) lists
 * exactly its own bindings; a parent level's bindings are not inherited.
 */
export type AIActionBinding<P = unknown> = {
  // the template id, or `${template.id}@${anchor}`: the panel and a scene compute the same id
  id: string;
  template: AIActionTemplate<P>;
  params: P;
  group: AIActionGroup;
  route?: string;
  disabled?: boolean;
  title?: string;
  running?: boolean;
  icon?: IIconProps;
  label?: string;
  selectedEntityIds?: number[];
  skill?: string;
  usesSelectedDocument?: boolean;
};

export const getAIActionBindingId = <P>(template: AIActionTemplate<P>, params: P): string => {
  const anchor = template.getAnchor?.(params);

  return anchor ? `${template.id}@${anchor}` : template.id;
};

const getDefaultGroup = (kind: AIActionKind): AIActionGroup =>
  kind === AIActionKind.Inline ? AIActionGroup.Inline : AIActionGroup.Assistant;

export const createAIActionBinding = <P>(
  template: AIActionTemplate<P>,
  params: P,
  options?: AIActionBindingOptions,
): AIActionBinding<P> => ({
  id: getAIActionBindingId(template, params),
  template,
  params,
  group: options?.group ?? getDefaultGroup(template.kind),
  route: options?.route,
  disabled: options?.disabled,
  title: options?.title,
  running: options?.running,
  icon: options?.icon,
  label: options?.label,
  selectedEntityIds: options?.selectedEntityIds,
  skill: options?.skill,
  usesSelectedDocument: options?.usesSelectedDocument,
});

export const getAIActionLabel = <P>(binding: AIActionBinding<P>, t: TFunction<string[]>): string =>
  binding.label ?? binding.template.getLabel(t, binding.params);

/**
 * The panel's view of a binding. Prompt and Process bindings become the `CopilotAction` the panel
 * lists and runs; Inline and Assistant bindings belong to the scene and return undefined.
 */
export const toCopilotAction = <P>(
  binding: AIActionBinding<P>,
  t: TFunction<string[]>,
): CopilotAction | undefined => {
  const { template, params } = binding;
  if (template.kind !== AIActionKind.Prompt && template.kind !== AIActionKind.Process) return undefined;

  const action = new CopilotAction();
  action.id = binding.id;
  action.name = template.getLabel(t, params);
  action.disabled = binding.disabled;
  action.route = binding.route;

  if (template.kind === AIActionKind.Prompt) {
    action.type = CopilotActionType.Prompt;
    action.prompt = template.getPrompt(t, params);
    action.acceptsSelection = template.acceptsSelection;
    action.skill = binding.skill;
    action.usesSelectedDocument = binding.usesSelectedDocument;

    return action;
  }

  action.type = CopilotActionType.Process;
  action.processId = template.getProcessId(params);
  action.acceptsSelection = template.acceptsSelection;
  action.status = TenantProcessStatus.NotStarted;
  action.runsInBackground = template.runsInBackground?.(params);

  return action;
};

/**
 * What a surface hands the bridge when the user picks a panel-run binding: the binding itself, so the
 * panel can run a binding no usage lists (a field's text actions), for a text field, the field with
 * the text the editor holds right now, and, for a prompt that takes a selection, the rows the scene
 * had selected (an empty array is the whole set).
 */
export type AIActionRequest = {
  binding: AIActionBinding;
  activeField?: CopilotActiveField;
  selectedEntityIds?: number[];
  // a Process binding: what the surface knows for the start (the data report job of the modal, PLAN-070; an
  // assessment's automation: the assessment task, the item and the criteria, PLAN-073)
  processStartInput?: Omit<CopilotProcessStartInput, 'entityIds'>;
  // a Process binding with the start input's entity: open the user's own run of that entity (running or finished)
  // instead of starting it; without such a run it starts as any request ("Open the result" of an AI outcome)
  open?: boolean;
};
